import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const GENERIC_ITEMS = [
  'diagram.compare.generic.item1',
  'diagram.compare.generic.item2',
  'diagram.compare.generic.item3',
  'diagram.compare.generic.item4',
  'diagram.compare.generic.item5',
];

const CUSTOM_ITEMS = [
  'diagram.compare.custom.item1',
  'diagram.compare.custom.item2',
  'diagram.compare.custom.item3',
  'diagram.compare.custom.item4',
  'diagram.compare.custom.item5',
  'diagram.compare.custom.item6',
  'diagram.compare.custom.item7',
];

export default function ComparisonDiagram({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const [active, setActive] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      {/* Animated connecting bridge between columns */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none z-10" style={{ overflow: 'visible' }}>
        <defs>
          <linearGradient id="bridgeGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#22D3C5" stopOpacity="0.3" />
            <stop offset="50%" stopColor="#7057FF" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#22D3C5" stopOpacity="0.3" />
          </linearGradient>
        </defs>

        {/* Animated data bridge lines */}
        {active && [0, 1, 2].map((i) => (
          <path
            key={i}
            d="M 220 80 Q 300 80 380 80"
            fill="none"
            stroke="url(#bridgeGrad)"
            strokeWidth="1"
            strokeDasharray="4 6"
            opacity="0.5"
          >
            <animate
              attributeName="stroke-dashoffset"
              from="20"
              to="0"
              dur={`${2 + i * 0.5}s`}
              repeatCount="indefinite"
            />
          </path>
        ))}

        {/* Central transformation node */}
        <g transform="translate(300, 80)">
          <circle r="18" fill="oklch(var(--primary-500))" opacity={active ? 0.9 : 0.3} className="transition-opacity duration-1000">
            <animate attributeName="r" values="16;20;16" dur="3s" repeatCount="indefinite" />
          </circle>
          <text textAnchor="middle" y="4" fill="#22D3C5" fontSize="10" fontFamily="var(--font-mono)" fontWeight="600">
            M
          </text>
          {active && (
            <circle r="24" fill="none" stroke="#7057FF" strokeWidth="0.5" opacity="0.4">
              <animate attributeName="r" values="24;30;24" dur="3s" repeatCount="indefinite" />
            </circle>
          )}
        </g>
      </svg>

      <div className="grid md:grid-cols-2 gap-6 md:gap-8 relative z-20">
        {/* Generic AI Column */}
        <div
          className={`relative p-6 rounded-xl border border-background-200/80 bg-background-50/60 backdrop-blur-sm transition-all duration-700 ${
            active ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-foreground-100 flex items-center justify-center">
              <i className="ri-global-line text-lg text-foreground-500" />
            </div>
            <div>
              <h4 className="text-sm font-heading font-semibold text-foreground-700">{t('diagram.compare.generic.title')}</h4>
              <p className="text-[10px] font-mono text-foreground-400 uppercase tracking-wider">{t('diagram.compare.generic.subtitle')}</p>
            </div>
          </div>

          {/* Items */}
          <ul className="space-y-3">
            {GENERIC_ITEMS.map((item, i) => (
              <li
                key={item}
                className={`flex items-center gap-2.5 text-sm text-foreground-600 transition-all duration-500 ${
                  active ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
                }`}
                style={{ transitionDelay: `${0.3 + i * 0.1}s` }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-foreground-300 shrink-0" />
                {t(item)}
              </li>
            ))}
          </ul>

          {/* Decorative */}
          <div className="absolute -top-2 -right-2 w-16 h-16 rounded-full bg-foreground-100/50 blur-xl" />
        </div>

        {/* Custom AI Column */}
        <div
          className={`relative p-6 rounded-xl border border-accent-200/60 bg-accent-50/30 backdrop-blur-sm transition-all duration-700 ${
            active ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}
          style={{ transitionDelay: '0.3s' }}
        >
          {/* Header */}
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-accent-100 flex items-center justify-center">
              <i className="ri-building-4-line text-lg text-accent-600" />
            </div>
            <div>
              <h4 className="text-sm font-heading font-semibold text-foreground-800">{t('diagram.compare.custom.title')}</h4>
              <p className="text-[10px] font-mono text-accent-600 uppercase tracking-wider">{t('diagram.compare.custom.subtitle')}</p>
            </div>
          </div>

          {/* Items */}
          <ul className="space-y-3">
            {CUSTOM_ITEMS.map((item, i) => (
              <li
                key={item}
                className={`flex items-center gap-2.5 text-sm text-foreground-700 transition-all duration-500 ${
                  active ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                }`}
                style={{ transitionDelay: `${0.5 + i * 0.1}s` }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-accent-400 shrink-0" />
                {t(item)}
              </li>
            ))}
          </ul>

          {/* Decorative */}
          <div className="absolute -bottom-2 -left-2 w-20 h-20 rounded-full bg-accent-200/30 blur-xl" />
          <div className="absolute top-4 right-4 w-8 h-8 border border-accent-300/30 rounded-lg rotate-12 animate-[morphFloat_5s_ease-in-out_infinite]" />
        </div>
      </div>
    </div>
  );
}